import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  hasWarrantyContact,
  isValidVin,
  isWarrantyEditable,
  normalizeVin,
  VIN_LENGTH,
  type Warranty,
  type WarrantyPrefill,
} from "@shared/modules/x-garanties";
import type { ClientDetail, Vehicle } from "@shared/modules/x-atelier";
import { ApiError } from "../../../core/lib/api";
import { useToastStore } from "../../../core/stores/toast";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Textarea } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { BackLink } from "../../../core/components/ui/back-link";
import { TermsEditor } from "../components/terms-editor";
import {
  createWarranty,
  emitWarranty,
  getAtelierClient,
  getPrefill,
  getWarranty,
  searchAtelierClients,
  updateWarranty,
  type AtelierClientRow,
  type WarrantyFields,
} from "../lib/api";
import { toDateInputValue } from "../lib/dates";

/** Valeurs du formulaire (chaînes, comme les champs). */
interface Draft {
  clientId: string;
  vehicleId: string;
  appointmentId: string | null;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  clientAddress: string;
  vehicleLabel: string;
  vin: string;
  mileage: string;
  serviceLabel: string;
  issuedAt: string;
  termsMd: string;
  notes: string;
}

function draftFromPrefill(p: WarrantyPrefill): Draft {
  return {
    clientId: p.clientId,
    vehicleId: p.vehicleId,
    appointmentId: p.appointmentId,
    clientName: p.clientName,
    clientPhone: p.clientPhone ?? "",
    clientEmail: p.clientEmail ?? "",
    clientAddress: p.clientAddress ?? "",
    vehicleLabel: p.vehicleLabel,
    vin: p.vin ?? "",
    mileage: p.mileage === null ? "" : String(p.mileage),
    serviceLabel: p.serviceLabel ?? "",
    issuedAt: "",
    termsMd: p.termsMd,
    notes: "",
  };
}

function draftFromWarranty(w: Warranty): Draft {
  return {
    clientId: w.clientId,
    vehicleId: w.vehicleId,
    appointmentId: w.appointmentId,
    clientName: w.clientName,
    clientPhone: w.clientPhone ?? "",
    clientEmail: w.clientEmail ?? "",
    clientAddress: w.clientAddress ?? "",
    vehicleLabel: w.vehicleLabel,
    vin: w.vin ?? "",
    mileage: w.mileage === null ? "" : String(w.mileage),
    serviceLabel: w.serviceLabel ?? "",
    issuedAt: toDateInputValue(w.issuedAt),
    termsMd: w.termsMd,
    notes: w.notes ?? "",
  };
}

/** Corps envoyé à l'API : champs vides → null, kilométrage en nombre. */
function toFields(d: Draft): WarrantyFields {
  const mileage = d.mileage.trim() === "" ? null : Number.parseInt(d.mileage, 10);
  return {
    clientName: d.clientName.trim(),
    clientPhone: d.clientPhone.trim() || null,
    clientEmail: d.clientEmail.trim() || null,
    clientAddress: d.clientAddress.trim() || null,
    vehicleLabel: d.vehicleLabel.trim(),
    vin: normalizeVin(d.vin) || null,
    mileage: mileage !== null && Number.isFinite(mileage) ? mileage : null,
    serviceLabel: d.serviceLabel.trim() || null,
    issuedAt: d.issuedAt || null,
    termsMd: d.termsMd,
    notes: d.notes.trim() || null,
  };
}

type Failure = "not_found" | "locked" | "error" | null;

/**
 * Nouvelle garantie (pré-remplie depuis ?rdv= ou ?client=&vehicle=, sinon un
 * sélecteur client → véhicule) et modification d'un brouillon ou d'une
 * garantie émise non signée. Le NIV est le seul champ qu'il faut
 * généralement saisir : il est mis en évidence.
 */
export function GarantieFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);

  const editing = Boolean(id);
  const [current, setCurrent] = useState<Warranty | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [busy, setBusy] = useState(false);
  const [showVinError, setShowVinError] = useState(false);

  const loadPrefill = useCallback(
    (source: { appointmentId?: string; clientId?: string; vehicleId?: string }) => {
      getPrefill(source)
        .then((p) => {
          setDraft(draftFromPrefill(p));
          setFailure(null);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === "vehicle_required") {
            push(
              "warning",
              "Ce rendez-vous n'a pas de véhicule",
              "Ajoutez-en un au client, puis reprenez.",
            );
          }
          setFailure(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
        });
    },
    [push],
  );

  useEffect(() => {
    if (id) {
      getWarranty(id)
        .then((w) => {
          if (!isWarrantyEditable(w.status)) {
            setFailure("locked");
            return;
          }
          setCurrent(w);
          setDraft(draftFromWarranty(w));
          setFailure(null);
        })
        .catch((err: unknown) =>
          setFailure(err instanceof ApiError && err.status === 404 ? "not_found" : "error"),
        );
      return;
    }
    const rdv = searchParams.get("rdv");
    const client = searchParams.get("client");
    const vehicle = searchParams.get("vehicle");
    if (rdv) loadPrefill({ appointmentId: rdv });
    else if (client && vehicle) loadPrefill({ clientId: client, vehicleId: vehicle });
  }, [id, searchParams, loadPrefill]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const vinOk = draft ? isValidVin(draft.vin) : false;
  const vinError =
    draft && draft.vin.trim() && !vinOk
      ? `Le NIV compte ${VIN_LENGTH} caractères (lettres et chiffres, sans I, O ni Q).`
      : showVinError && !vinOk
        ? "Le NIV est obligatoire pour émettre."
        : undefined;

  const save = async (): Promise<Warranty | null> => {
    if (!draft) return null;
    if (!draft.clientName.trim() || !draft.vehicleLabel.trim()) {
      push("danger", "Nom du client et véhicule obligatoires");
      return null;
    }
    if (draft.vin.trim() && !vinOk) {
      push("danger", "NIV invalide", `Un NIV compte ${VIN_LENGTH} caractères.`);
      return null;
    }
    const fields = toFields(draft);
    if (!hasWarrantyContact(fields)) {
      push(
        "danger",
        "Téléphone ou courriel obligatoire",
        "La garantie est envoyée au client pour signature : indiquez au moins un moyen de le joindre.",
      );
      return null;
    }
    if (editing && current) return updateWarranty(current.id, fields);
    return createWarranty({
      ...fields,
      ...(draft.appointmentId
        ? { appointmentId: draft.appointmentId }
        : { clientId: draft.clientId, vehicleId: draft.vehicleId }),
    });
  };

  const saveDraft = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const saved = await save();
      if (!saved) return;
      push("success", editing ? "Garantie enregistrée" : "Brouillon enregistré", saved.number);
      navigate(`/garanties/${saved.id}`);
    } catch (err) {
      push("danger", "Enregistrement impossible", describe(err));
    } finally {
      setBusy(false);
    }
  };

  const saveAndEmit = async () => {
    if (busy || !draft) return;
    if (!vinOk) {
      setShowVinError(true);
      push(
        "warning",
        "NIV requis",
        "Saisissez le numéro d'identification du véhicule avant d'émettre.",
      );
      return;
    }
    setBusy(true);
    try {
      const saved = await save();
      if (!saved) return;
      const emitted = await emitWarranty(saved.id);
      push("success", "Garantie émise", emitted.number);
      navigate(`/garanties/${emitted.id}`);
    } catch (err) {
      push("danger", "Émission impossible", describe(err));
    } finally {
      setBusy(false);
    }
  };

  if (failure) {
    return (
      <div className="page">
        <p className="garantie-backlink">
          <BackLink fallback="/garanties" />
        </p>
        <p className="empty">
          {failure === "not_found"
            ? "Introuvable — le rendez-vous, le client ou la garantie n'existe plus."
            : failure === "locked"
              ? "Cette garantie est signée, expirée ou annulée : elle ne se modifie plus."
              : "Formulaire indisponible — réessayez dans un instant."}
        </p>
      </div>
    );
  }

  if (!draft) {
    const waitingForSource = !editing && !searchParams.get("rdv") && !searchParams.get("client");
    return (
      <div className="page garantie-form">
        <p className="garantie-backlink">
          <BackLink fallback="/garanties" />
        </p>
        <PageHeader
          title="Nouvelle garantie"
          subtitle={
            waitingForSource ? "Choisissez le client, puis le véhicule couvert." : "Chargement…"
          }
        />
        {waitingForSource ? (
          <SourcePicker onPick={(clientId, vehicleId) => loadPrefill({ clientId, vehicleId })} />
        ) : null}
      </div>
    );
  }

  const canEmitHere = !editing || current?.status === "brouillon";

  return (
    <div className="page garantie-form">
      <p className="garantie-backlink">
        <BackLink fallback={editing && current ? `/garanties/${current.id}` : "/garanties"} />
      </p>
      <PageHeader
        title={editing && current ? `Modifier ${current.number}` : "Nouvelle garantie"}
        subtitle="Les coordonnées et le véhicule sont copiés tels quels sur le certificat."
        actions={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => void saveDraft()}>
              {editing ? "Enregistrer" : "Enregistrer le brouillon"}
            </Button>
            {canEmitHere ? (
              <Button variant="primary" disabled={busy} onClick={() => void saveAndEmit()}>
                Émettre
              </Button>
            ) : null}
          </>
        }
      />

      <form
        className="garantie-form__body"
        onSubmit={(e) => {
          e.preventDefault();
          void saveDraft();
        }}
      >
        <section className="form-section">
          <div className="form-section__head">
            <h2 className="form-section__title">Client</h2>
            <p className="form-section__hint">
              Copie figée : modifier ici ne change pas la fiche client. Téléphone ou courriel
              obligatoire : la garantie est envoyée au client pour signature, par courriel avec le
              certificat PDF, sinon par texto.
            </p>
          </div>
          <div className="form-section__grid">
            <Field label="Nom">
              <Input
                value={draft.clientName}
                onChange={(e) => set("clientName", e.target.value)}
                required
              />
            </Field>
            <Field label="Téléphone">
              <Input
                value={draft.clientPhone}
                onChange={(e) => set("clientPhone", e.target.value)}
              />
            </Field>
            <Field label="Courriel">
              <Input
                type="email"
                value={draft.clientEmail}
                onChange={(e) => set("clientEmail", e.target.value)}
              />
            </Field>
          </div>
          <div className="form-section__grid form-section__grid--full">
            <Field label="Adresse">
              <Input
                value={draft.clientAddress}
                onChange={(e) => set("clientAddress", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <h2 className="form-section__title">Véhicule</h2>
            <p className="form-section__hint">
              Le NIV (17 caractères) est obligatoire pour émettre la garantie.
            </p>
          </div>
          <div className="form-section__grid form-section__grid--full">
            <Field label="NIV — numéro d'identification du véhicule" error={vinError}>
              <Input
                className="garantie-form__vin"
                value={draft.vin}
                onChange={(e) => set("vin", e.target.value.toUpperCase())}
                maxLength={VIN_LENGTH + 4}
                autoComplete="off"
                spellCheck={false}
                placeholder="1HGCM82633A004352"
                autoFocus={!editing}
              />
            </Field>
          </div>
          <div className="form-section__grid">
            <Field label="Véhicule">
              <Input
                value={draft.vehicleLabel}
                onChange={(e) => set("vehicleLabel", e.target.value)}
                required
              />
            </Field>
            <Field label="Kilométrage">
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                value={draft.mileage}
                onChange={(e) => set("mileage", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <h2 className="form-section__title">Service</h2>
          </div>
          <div className="form-section__grid">
            <Field label="Service couvert" hint="Repris des lignes du rendez-vous s'il y en a.">
              <Input
                value={draft.serviceLabel}
                onChange={(e) => set("serviceLabel", e.target.value)}
              />
            </Field>
            <Field
              label="Date d'émission"
              hint="Laissez vide pour prendre la date du jour à l'émission ; l'expiration suit, dix ans plus tard."
            >
              <Input
                type="date"
                value={draft.issuedAt}
                onChange={(e) => set("issuedAt", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <h2 className="form-section__title">Termes de la garantie</h2>
            <p className="form-section__hint">
              Pré-remplis depuis le modèle ; ajustez au besoin pour ce certificat seulement.
            </p>
          </div>
          <TermsEditor value={draft.termsMd} onChange={(v) => set("termsMd", v)} />
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <h2 className="form-section__title">Notes</h2>
            <p className="form-section__hint">Imprimées sur le certificat.</p>
          </div>
          <div className="form-section__grid form-section__grid--full">
            <Field label="Notes">
              <Textarea
                rows={3}
                value={draft.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <div className="garantie-form__actions">
          <Button type="submit" variant="ghost" disabled={busy}>
            {editing ? "Enregistrer" : "Enregistrer le brouillon"}
          </Button>
          {canEmitHere ? (
            <Button
              type="button"
              variant="primary"
              disabled={busy}
              onClick={() => void saveAndEmit()}
            >
              Émettre
            </Button>
          ) : null}
        </div>
      </form>
    </div>
  );
}

function describe(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  switch (err.code) {
    case "vin_required":
      return "Le NIV est obligatoire.";
    case "vin_invalid":
      return `Un NIV compte ${VIN_LENGTH} caractères, sans I, O ni Q.`;
    case "locked":
      return "Cette garantie n'est plus modifiable.";
    case "contact_required":
      return "Indiquez au moins un téléphone ou un courriel.";
    case "vehicle_client_mismatch":
      return "Ce véhicule n'appartient pas à ce client.";
    case "invalid_input":
      return "Vérifiez les champs saisis.";
    default:
      return undefined;
  }
}

/** Sans rendez-vous ni client dans l'adresse : recherche du client, puis choix du véhicule. */
function SourcePicker({ onPick }: { onPick: (clientId: string, vehicleId: string) => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AtelierClientRow[]>([]);
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchAtelierClients(q)
        .then((list) => {
          if (!cancelled) {
            setResults(list.slice(0, 8));
            setFailed(false);
          }
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const pickClient = (id: string) => {
    getAtelierClient(id)
      .then(setDetail)
      .catch(() => setFailed(true));
  };

  return (
    <div className="card garantie-picker">
      <Field label="Client" hint="Nom, téléphone, immatriculation ou NIV.">
        <Input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setDetail(null);
          }}
          placeholder="Rechercher un client…"
          autoFocus
        />
      </Field>
      {failed ? (
        <p className="garantie-picker__hint">Recherche indisponible pour l'instant.</p>
      ) : null}
      {!detail && results.length > 0 ? (
        <ul className="garantie-picker__results">
          {results.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="garantie-picker__result"
                onClick={() => pickClient(c.id)}
              >
                <span className="garantie-picker__result-name">{c.name}</span>
                <span className="garantie-picker__result-meta">
                  {[c.phone, `${c.vehicleCount} véhicule${c.vehicleCount > 1 ? "s" : ""}`]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!detail && query.trim().length >= 2 && results.length === 0 && !failed ? (
        <p className="garantie-picker__hint">Aucun client trouvé.</p>
      ) : null}
      {detail ? (
        <div className="garantie-picker__vehicles">
          <p className="garantie-picker__picked">
            <strong>{detail.client.name}</strong> — choisissez le véhicule couvert :
          </p>
          {detail.vehicles.length === 0 ? (
            <p className="garantie-picker__hint">
              Ce client n'a aucun véhicule. Ajoutez-en un dans sa fiche, puis revenez.
            </p>
          ) : (
            <ul className="garantie-picker__results">
              {detail.vehicles.map((v: Vehicle) => (
                <li key={v.id}>
                  <button
                    type="button"
                    className="garantie-picker__result"
                    onClick={() => onPick(detail.client.id, v.id)}
                  >
                    <span className="garantie-picker__result-name">
                      {[v.year, v.make, v.model].filter(Boolean).join(" ")}
                    </span>
                    <span className="garantie-picker__result-meta">
                      {[v.plate, v.vin ? `NIV ${v.vin}` : "NIV manquant"]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
